import { Injectable } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { Howl } from 'howler';
import { MessageService } from 'primeng/api';

import { ConversationLog, ConversationState } from '@pwp-common';

@Injectable({
  providedIn: 'root',
})
export class IncomingChatNotificationService {
  /////////////////////////////////////////////////////////////////////////////////////////////
  // State
  /////////////////////////////////////////////////////////////////////////////////////////////

  public readonly notifiedConversationIds = new Set<string>();

  public isInitialized = false;

  private readonly notificationSound = new Howl({
    src: ['assets/sounds/incoming-chat-notification.mp3'],
    volume: 0.7,
  });

  /////////////////////////////////////////////////////////////////////////////////////////////
  // Public Methods
  /////////////////////////////////////////////////////////////////////////////////////////////

  public processConversationLogs(conversationLogs: ConversationLog[], messageService: MessageService): void {
    const serviceRequestedConversations = conversationLogs.filter(
      (log) => log.getState() === ConversationState.serviceRequested,
    );

    if (!this.isInitialized) {
      for (const conversation of serviceRequestedConversations) {
        this.notifiedConversationIds.add(conversation.getId());
      }
      this.isInitialized = true;
      return;
    }

    this.notifyNewConversations(serviceRequestedConversations, messageService);
  }

  public notifyNewConversations(
    serviceRequestedConversations: ConversationLog[],
    messageService: MessageService,
  ): void {
    const newConversations = serviceRequestedConversations.filter(
      (conversation) => !this.notifiedConversationIds.has(conversation.getId()),
    );

    for (const conversation of newConversations) {
      this.notifiedConversationIds.add(conversation.getId());
      this.showNotification(messageService);
    }
  }

  private showNotification(messageService: MessageService): void {
    const summary = translate('conversation-logs-query-editor.incomingChatToastSummary');
    const detail = translate('conversation-logs-query-editor.incomingChatToastDetail');

    messageService.add({
      severity: 'info',
      summary,
      detail,
      life: 5000,
      closable: true,
      styleClass: 'incoming-chat-notification',
      contentStyleClass: 'incoming-chat-notification-content',
    });

    this.playNotificationSound();
  }

  private playNotificationSound(): void {
    try {
      this.notificationSound.play();
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  }
}
